import { supabase } from "@/integrations/supabase/client";
import { captureError } from "@/lib/sentry";

// Shared promise to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<void> | null = null;

/**
 * Checks if an error is a JWT token expiration error
 */
export function isTokenError(error: Error | string): boolean {
  const message = typeof error === "string" ? error : error.message || "";
  const lower = message.toLowerCase();
  return (
    lower.includes("jwt") ||
    lower.includes("token") ||
    lower.includes("expired") ||
    lower.includes("invalid")
  );
}

/**
 * Handles Realtime subscription errors, particularly JWT expiration.
 * Uses a shared promise to prevent multiple simultaneous refreshes.
 *
 * @param error - The error from the subscription callback
 * @param context - A string identifying where the error occurred (for logging)
 * @returns Promise that resolves when refresh is complete
 */
export async function handleRealtimeError(
  error: Error,
  context: string,
): Promise<void> {
  if (!isTokenError(error)) {
    // Not a token error, just log to Sentry
    captureError(new Error(`Realtime subscription failed: ${error.message}`), {
      context,
      isTokenError: false,
    });
    return;
  }

  console.log(
    `Token error detected in ${context}, attempting session refresh...`,
  );

  // Use shared promise to prevent duplicate refresh attempts
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const { data, error: refreshError } =
          await supabase.auth.refreshSession();

        if (refreshError) {
          console.error("Failed to refresh session:", refreshError);
          // Only capture to Sentry when refresh FAILS (unrecoverable)
          // Recoverable JWT errors (successful refresh) don't need to be reported
          captureError(
            new Error(
              `Realtime JWT error (unrecoverable): ${refreshError.message}`,
            ),
            {
              context,
              isTokenError: true,
              refreshFailed: true,
            },
          );
          return;
        }

        if (data.session?.access_token) {
          supabase.realtime.setAuth(data.session.access_token);
          console.log("Session refreshed and Realtime auth updated");
        }
      } catch (err) {
        console.error("Error during session refresh:", err);
        captureError(err instanceof Error ? err : new Error(String(err)), {
          context: "realtime_token_refresh_error",
        });
      } finally {
        // Clear the promise after a short delay to allow rapid retries to share
        setTimeout(() => {
          refreshPromise = null;
        }, 1000);
      }
    })();
  }

  await refreshPromise;
  // No Sentry capture here - either refresh succeeded (no need to report)
  // or failed (already captured above with "unrecoverable" label)
}

/**
 * Creates a subscription callback that handles errors automatically.
 * Use this when creating Realtime subscriptions.
 *
 * @param context - A string identifying the subscription (for logging)
 * @returns A callback function to pass to .subscribe()
 */
export function createSubscribeCallback(context: string) {
  return (status: string, error?: Error) => {
    if (error) {
      console.error(`${context} subscription error:`, error);
      handleRealtimeError(error, context);
    }
  };
}

/**
 * Type-safe extraction of a string field from a Realtime payload.
 * Returns null if the payload is not an object or field is not a string.
 *
 * @param payload - The payload.new or payload.old from a Realtime callback
 * @param field - The field name to extract
 * @returns The field value as a string, or null if not found/invalid
 */
export function extractPayloadString(
  payload: unknown,
  field: string,
): string | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  const value = p[field];
  return typeof value === "string" ? value : null;
}

/**
 * Type-safe extraction of a record (object) from a Realtime payload.
 * Returns null if the payload is not an object or doesn't have an id field.
 *
 * @param payload - The payload.new or payload.old from a Realtime callback
 * @returns The payload as a Record with id, or null if invalid
 */
export function extractPayloadRecord<T extends { id: string }>(
  payload: unknown,
): T | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  if (typeof p.id !== "string") return null;
  return p as unknown as T;
}
